import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    FormHelperText,
    IconButton,
    Drawer,
    Divider,
    CircularProgress,
} from "@mui/material";
import { CancelOutlined } from "@mui/icons-material";
import apiClient from "../../api/apiClient";
import { showErrorToast, showSuccessToast } from "../../toastUtils";
import { createUser, updateUser } from "../../api/user";

export default function InviteUserDrawer({ isOpen, onClose, setUserData, user, fetchUserData }) {
    const [formData, setFormData] = useState({
        full_name: "",
        email: "",
        role: "",
    });
    const [errors, setErrors] = useState({});
    const [roles, setRoles] = useState([]);
    const [isEdited, setIsEdited] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user && roles.length > 0) {
            setFormData({
                full_name: user.full_name || "",
                email: user.email || "",
                role: roles.find(role => role.role_name === user.role)?.role_id || "",
            });
        }
    }, [user, roles]);


    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await apiClient.get("/admin/roles");
                if (response?.data?.success) {
                    setRoles(response.data.data || []);
                }
            } catch (error) {
                console.error("Error fetching roles:", error);
            }
        };

        fetchRoles();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: "",
        }));
        setIsEdited(true)
    };

    const validate = () => {
        const newErrors = {};

        const full_name = formData.full_name?.trim();
        const trimmedEmail = formData.email?.trim();

        if (!full_name) {
            newErrors.full_name = "Name is required";
        }

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!trimmedEmail) {
            newErrors.email = "Email is required";
        } else if (!emailRegex.test(trimmedEmail)) {
            newErrors.email = "Invalid email format";
        }

        if (!formData.role) {
            newErrors.role = "Role is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        const payload = {
            full_name: formData.full_name,
            email: formData.email,
            role_id: formData.role,
        };

        setLoading(true);
        try {
            let response;
            const editingUserId = user?.user_id; // Assuming user is the object passed to the component
            if (editingUserId) {
                // Editing existing user
                response = await updateUser(editingUserId, payload); // You need to implement this API call
                if (response?.success) {
                    // setUserData((prev) =>
                    //     prev.map((user) =>
                    //         user.user_id === editingUserId
                    //             ? {
                    //                 ...user,
                    //                 full_name: response.data.user_data.full_name,
                    //                 // email: response.data.user_data.email,
                    //                 role: roles.find(role => response.data.user_data.role_id === role.role_id)?.role_name,
                    //             }
                    //             : user
                    //     )
                    // );
                    await fetchUserData()
                    showSuccessToast(response.message);
                } else {
                    showErrorToast(response?.message || "Failed to update user. Please try again.");
                }
            } else {
                // Creating new user
                response = await createUser(payload);
                // console.log(response, 'create');

                if (response?.success) {
                    // const newUser = {
                    //     user_id: response.data.user_id,
                    //     full_name: response.data.full_name,
                    //     email: response.data.email,
                    //     role: roles.find(role => response.data.role_id === role.role_id)?.role_name,
                    // };
                    // setUserData((prev) => [newUser, ...prev]);
                    await fetchUserData()
                    showSuccessToast(response.message);
                } else {
                    showErrorToast(response?.message || "Failed to create user. Please try again.");
                }
            }

            onClose(); // Close modal/drawer after success
        } catch (error) {
            console.error("User operation error:", error);
            const serverMsg = error?.message;
            showErrorToast(serverMsg || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
            setIsEdited(false)
        }
    };

    const handleClose = () => {
        onClose()
        setIsEdited(false)
    }
    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    width: {
                        xs: "100%",
                        sm: '500px',
                    },
                    borderTopLeftRadius: 8,
                    borderBottomLeftRadius: 8,
                    boxSizing: "border-box",
                },
            }}
        >
            <Box sx={{ p: 3 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: "20px",
                    }}
                >

                    <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
                            <IconButton onClick={onClose} size="small" sx={{ mt: -0.5 }}>
                                <CancelOutlined />
                            </IconButton>
                        </Box>
                        <Typography
                            variant="h6"
                            component="h2"
                            fontWeight="bold"
                            sx={{ color: '#FF6348' }}
                        >
                            {user?.user_id ? "Update" : "Invite a"} User
                        </Typography>
                        <Typography variant="body2" color="#989898" sx={{ mt: 1 }}>
                            Add information below to invite a user
                        </Typography>
                    </Box>
                </Box>
            </Box>
            <Divider />
            <Box
                component="form"
                onSubmit={handleSubmit}
            >
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        p: 3,
                    }}
                >
                    {/* Full Name */}
                    <FormControl fullWidth error={Boolean(errors.full_name)}>
                        <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                            Full Name
                            <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                        </Typography>
                        <TextField
                            name="full_name"
                            value={formData.full_name}
                            onChange={handleChange}
                            placeholder="Enter Full Name"
                            variant="outlined"
                            size="small"
                        />
                        {errors.full_name && <FormHelperText>{errors.full_name}</FormHelperText>}
                    </FormControl>

                    {/* Email */}
                    <FormControl fullWidth error={Boolean(errors.email)}>
                        <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                            Email
                            <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                        </Typography>
                        <TextField
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter Email"
                            variant="outlined"
                            size="small"
                            disabled={user?.user_id} // Disable email field if editing an existing user
                        />
                        {errors.email && <FormHelperText>{errors.email}</FormHelperText>}
                    </FormControl>

                    {/* Role Selection */}
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            User Role
                        </Typography>
                        <FormControl fullWidth error={Boolean(errors.role)}>
                            <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                                Select Role
                                <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                            </Typography>

                            <Select
                                name="role"
                                value={formData.role}
                                onChange={handleChange}
                                size="small"
                                displayEmpty
                                renderValue={(selected) => {
                                    // Find the selected role by matching role_id
                                    const selectedRole = roles.find(role => role.role_id === selected);
                                    // return selectedRole ? selectedRole.role_name.charAt(0).toUpperCase() + selectedRole.role_name.slice(1) : <span style={{ color: "#aaa" }}>Role</span>;
                                    return selectedRole ? selectedRole.role_name : <span style={{ color: "#aaa" }}>Role</span>;
                                }}
                            >
                                {roles && roles.length > 0 &&
                                    roles.map(role => (
                                        <MenuItem key={role.role_id} value={role.role_id}>
                                            {/* {role.role_name.charAt(0).toUpperCase() + role.role_name.slice(1)} */}
                                            {role.role_name}
                                        </MenuItem>
                                    ))
                                }
                            </Select>

                            {errors.role && <FormHelperText>{errors.role}</FormHelperText>}
                        </FormControl>
                    </Box>
                </Box>

                <Box sx={{ position: 'absolute', bottom: 0, width: '100%' }}>
                    <Divider />
                    <Box sx={{ display: "flex", alignItems: 'center', gap: 2, p: 3 }}>
                        <Button variant="outlined" type="submit"
                            disabled={loading || !isEdited}
                            sx={{
                                color: '#525455',
                                fontWeight: 600,
                                borderWidth: 2,
                                borderColor: '#FF6348',
                                '&:hover': {
                                    backgroundColor: '#FF6348',
                                    color: '#fff'
                                }
                            }}>
                            {loading ? (<CircularProgress size={20} color="#fff" />) : user.user_id ? 'Update' : 'Submit'}
                        </Button>
                        <Button variant="outlined"
                            disabled={loading}
                            onClick={handleClose}
                            sx={{
                                color: '#525455',
                                fontWeight: 600,
                                borderWidth: 2,
                                borderColor: '#CECECE',
                                '&:hover': {
                                    backgroundColor: '#CECECE',
                                    // color: '#fff'
                                }
                            }}>
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
}